import React, { createContext, useContext, useState } from 'react';
import { Api } from '../api';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createSession = async (designType) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Api.post('sessions', {
        designType,
        designData: {}
      });
      const session = response.data;
      setCurrentSession(session);
      return session;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create session';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Api.get(`sessions/${sessionId}`);
      setCurrentSession(response.data);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load session';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateSession = async (sessionId, updateData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Api.patch(`sessions/${sessionId}`, updateData);
      setCurrentSession(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update session';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const exportSession = async (sessionId, email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Api.post(`sessions/${sessionId}/export`, {
        email
      });
      setCurrentSession(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to export session';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearSession = () => {
    setCurrentSession(null);
    setError(null);
  };

  const value = {
    currentSession,
    loading,
    error,
    createSession,
    loadSession,
    updateSession,
    exportSession,
    clearSession,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}; 